// src/Pages/GeneralGramSabhaStatusAdmin.jsx
// Role-aware General Gram Sabha Status Report
//
//  Role 1  (Admin)        → District table → Block modal → GP modal
//  Role 8  (CEO / ZP)     → District table (own district via API) → Block modal → GP modal
//  Role 7  (BDPO)         → Block table   → GP modal
//  Role 2  (Gram Sachiv)  → GP table      (no drill-down)
//  Role 3  (Sarpanch)     → GP table      (no drill-down)
//
// Date-filter contract with backend
//   • development_work + amrit_sarovar  → send  dev_amrit_all_time: true  (backend returns all-time data)
//   • all other agenda columns          → send  date_from: "YYYY-08-13"   (backend filters from 13 Aug)

import { useEffect, useState, useMemo } from "react";
import { Table, Button, Spin, Alert, Space, message, Modal, Tag } from "antd";
import {
    ReloadOutlined,
    FileExcelOutlined,
    FilePdfOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import { getGeneralGramSabhaStatusAdmin } from "../api/User";

/* ─────────────────────────────────────────────────────────────────────────── */
/*  STATIC FALLBACK – 22 Haryana districts                                     */
/* ─────────────────────────────────────────────────────────────────────────── */
const HARYANA_DISTRICTS = [
    { district_name: "AMBALA", district_code: "01", total_panchayat: 400 },
    { district_name: "BHIWANI", district_code: "02", total_panchayat: 312 },
    { district_name: "CHARKHI DADRI", district_code: "03", total_panchayat: 167 },
    { district_name: "FARIDABAD", district_code: "04", total_panchayat: 100 },
    { district_name: "FATEHABAD", district_code: "05", total_panchayat: 259 },
    { district_name: "GURUGRAM", district_code: "06", total_panchayat: 158 },
    { district_name: "HISAR", district_code: "07", total_panchayat: 309 },
    { district_name: "JHAJJAR", district_code: "08", total_panchayat: 250 },
    { district_name: "JIND", district_code: "09", total_panchayat: 300 },
    { district_name: "KAITHAL", district_code: "10", total_panchayat: 279 },
    { district_name: "KARNAL", district_code: "11", total_panchayat: 395 },
    { district_name: "KURUKSHETRA", district_code: "12", total_panchayat: 403 },
    { district_name: "MAHENDRAGARH", district_code: "13", total_panchayat: 343 },
    { district_name: "NUH", district_code: "14", total_panchayat: 325 },
    { district_name: "PALWAL", district_code: "15", total_panchayat: 263 },
    { district_name: "PANCHKULA", district_code: "16", total_panchayat: 135 },
    { district_name: "PANIPAT", district_code: "17", total_panchayat: 178 },
    { district_name: "REWARI", district_code: "18", total_panchayat: 365 },
    { district_name: "ROHTAK", district_code: "19", total_panchayat: 142 },
    { district_name: "SIRSA", district_code: "20", total_panchayat: 339 },
    { district_name: "SONIPAT", district_code: "21", total_panchayat: 318 },
    { district_name: "YAMUNANAGAR", district_code: "22", total_panchayat: 490 },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Pure helpers                                                                */
/* ─────────────────────────────────────────────────────────────────────────── */
const sumKey = (arr, key) => arr.reduce((s, i) => s + (Number(i[key]) || 0), 0);
const numSort = (a, b, key) => (a[key] || 0) - (b[key] || 0);
const strSort = (a, b, key) =>
    (a[key] || "").toString().toLowerCase()
        .localeCompare((b[key] || "").toString().toLowerCase());

const clickStyle = {
    color: "#1677ff", cursor: "pointer",
    textDecoration: "underline", fontWeight: 500,
};

/* ── Badge helpers ── */
const AllTimeTag = () => <Tag color="green" style={{ marginLeft: 4, fontSize: 10 }}>All time</Tag>;
const From13AugTag = () => <Tag color="orange" style={{ marginLeft: 4, fontSize: 10 }}>From 13 Aug</Tag>;

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Dynamic totals banner                                                       */
/* ─────────────────────────────────────────────────────────────────────────── */
const TotalsBanner = ({ data, fields, recordLabel }) => {
    const totals = useMemo(
        () => fields.reduce((acc, { key }) => ({
            ...acc, [key]: sumKey(data, key),
        }), {}),
        [data, fields]
    );

    return (
        <div style={{
            marginTop: 10, padding: "10px 14px", background: "#f0f9ff",
            borderRadius: 6, border: "1px solid #bae0ff",
            display: "flex", flexWrap: "wrap", gap: 14, fontSize: 13, flexShrink: 0,
        }}>
            <span>
                <strong>Total {recordLabel}:</strong>{" "}
                <strong style={{ color: "#1677ff" }}>{data.length}</strong>
            </span>
            {fields.map(({ label, key }) => (
                <span key={key}>
                    {label}: <strong>{totals[key] ?? 0}</strong>
                </span>
            ))}
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Shared agenda column definitions                                            */
/*  NOTE: "Total GP Conducted GS" is a derived/aggregate col — treated as      */
/*        "From 13 Aug" here but the backend decides the actual filter.         */
/* ─────────────────────────────────────────────────────────────────────────── */
const AGENDA_COLS = [
    {
        title: "Total GP Conducted Gram Sabha",
        titleText: "Total GP Conducted Gram Sabha",
        dataIndex: "total_gp_conducted_gs", width: 200, align: "center",
        sorter: (a, b) => numSort(a, b, "total_gp_conducted_gs"),
        render: (v) => v || 0,
    },
    {
        title: "Development Work",
        titleText: "Development Work",
        dataIndex: "development_work", width: 160, align: "center",
        sorter: (a, b) => numSort(a, b, "development_work"),
        render: (v) => v || 0,
    },
    {
        title: "Amrit Sarovar",
        titleText: "Amrit Sarovar",
        dataIndex: "amrit_sarovar", width: 140, align: "center",
        sorter: (a, b) => numSort(a, b, "amrit_sarovar"),
        render: (v) => v || 0,
    },
    {
        title: "Status of Drug Addiction",
        titleText: "Status of Drug Addiction",
        dataIndex: "drug_addiction", width: 200, align: "center",
        sorter: (a, b) => numSort(a, b, "drug_addiction"),
        render: (v) => v || 0,
    },
    {
        title: "Village Drinking Water Supply Status",
        titleText: "Village Drinking Water Supply Status",
        dataIndex: "drinking_water", width: 220, align: "center",
        sorter: (a, b) => numSort(a, b, "drinking_water"),
        render: (v) => v || 0,
    },
    {
        title: "Cleanliness, Solid Waste Management, Inspection of Government Building",
        titleText: "Cleanliness, SWM & Inspection",
        dataIndex: "cleanliness", width: 300, align: "center",
        sorter: (a, b) => numSort(a, b, "cleanliness"),
        render: (v) => v || 0,
    },
    {
        title: "Wastewater Management",
        titleText: "Wastewater Management",
        dataIndex: "wastewater", width: 180, align: "center",
        sorter: (a, b) => numSort(a, b, "wastewater"),
        render: (v) => v || 0,
    },
];

/* banner fields (for TotalsBanner) */
const AGENDA_BANNER_FIELDS = [
    { label: "GP Conducted GS", key: "total_gp_conducted_gs" },
    { label: "Development Work", key: "development_work" },
    { label: "Amrit Sarovar", key: "amrit_sarovar" },
    { label: "Drug Addiction", key: "drug_addiction" },
    { label: "Drinking Water", key: "drinking_water" },
    { label: "Cleanliness & Waste Mgmt", key: "cleanliness" },
    { label: "Wastewater Mgmt", key: "wastewater" },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/*  MODAL WRAPPER (shared for Block and GP modals)                              */
/* ─────────────────────────────────────────────────────────────────────────── */
const DrillModal = ({
    open, onCancel, title, loading, data, columns,
    scroll, recordLabel, bannerFields, extraBannerFields = [],
    page, pageSize, onPageChange, onExcelExport,
}) => (
    <Modal
        open={open}
        onCancel={onCancel}
        width={1500}
        destroyOnClose
        footer={null}
        closable
        style={{ top: 20 }}
        bodyStyle={{ padding: "16px 0 0 0", overflow: "hidden" }}
        title={
            <div style={{ paddingRight: 40 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 16, fontWeight: 600 }}>{title}</span>
                    <Button
                        type="primary" icon={<FileExcelOutlined />}
                        onClick={onExcelExport}
                        disabled={loading || !data.length}
                    >
                        Export Excel
                    </Button>
                </div>
            </div>
        }
    >
        {loading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
                <Spin size="large" />
                <p style={{ marginTop: 10, color: "#666", fontWeight: 500 }}>Loading data...</p>
            </div>
        ) : (
            <div style={{ display: "flex", flexDirection: "column", height: "calc(80vh - 110px)", overflow: "hidden" }}>
                <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", paddingRight: 4 }}>
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="key"
                        bordered size="small"
                        scroll={scroll}
                        pagination={{
                            current: page, pageSize, total: data.length,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50", "100"],
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                        }}
                        onChange={(p) => onPageChange(p.current, p.pageSize)}
                    />
                </div>
                {data.length > 0 && (
                    <TotalsBanner
                        data={data}
                        fields={[...extraBannerFields, ...bannerFields]}
                        recordLabel={recordLabel}
                    />
                )}
            </div>
        )}
    </Modal>
);

/* ─────────────────────────────────────────────────────────────────────────── */
/*  COMPONENT                                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */
const GeneralGramSabhaStatusAdmin = ({ dateFilter }) => {
    const roleId = sessionStorage.getItem("role_id");
    const userId = sessionStorage.getItem("user_id");
    const token = sessionStorage.getItem("token");

    /* role flags */
    const isAdmin = roleId === "1";
    const isCEO = roleId === "8";
    const isBDPO = roleId === "7";
    const isGramSachiv = roleId === "2";
    const isSarpanch = roleId === "3";

    const isDistrictView = isAdmin || isCEO;
    const isBlockView = isBDPO;
    const isGPView = isGramSachiv || isSarpanch;
    const initialLevel = isGPView ? "gp" : isBlockView ? "block" : "district";

    /* ── Main table state ── */
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    /* ── Block modal state ── */
    const [blockOpen, setBlockOpen] = useState(false);
    const [blockTitle, setBlockTitle] = useState("");
    const [blockData, setBlockData] = useState([]);
    const [blockLoading, setBlockLoading] = useState(false);
    const [blockPage, setBlockPage] = useState(1);
    const [blockPgSize, setBlockPgSize] = useState(10);

    /* ── GP modal state ── */
    const [gpOpen, setGpOpen] = useState(false);
    const [gpTitle, setGpTitle] = useState("");
    const [gpData, setGpData] = useState([]);
    const [gpLoading, setGpLoading] = useState(false);
    const [gpPage, setGpPage] = useState(1);
    const [gpPgSize, setGpPgSize] = useState(10);

    /* ================================================================
       BUILD API PAYLOAD
    ================================================================ */
    const buildPayload = (overrides = {}) => ({
        user_id: userId,
        role_id: roleId,
        level: initialLevel,
        dev_amrit_all_time: true,   // BE applies all-time for dev_work & amrit_sarovar
        ...overrides,
    });

    /* ================================================================
       FETCH – main table
    ================================================================ */
    const fetchReport = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await getGeneralGramSabhaStatusAdmin(token, buildPayload());

            if (res?.success && Array.isArray(res.data) && res.data.length) {
                setData(res.data.map((row, i) => ({ key: i + 1, sr_no: i + 1, ...row })));
            } else {
                // Graceful fallback for district view
                if (isDistrictView) {
                    setData(HARYANA_DISTRICTS.map((d, i) => ({
                        key: i + 1, sr_no: i + 1,
                        district_name: d.district_name, district_code: d.district_code,
                        total_panchayat: d.total_panchayat,
                        total_gp_conducted_gs: 0, development_work: 0,
                        amrit_sarovar: 0, drug_addiction: 0,
                        drinking_water: 0, cleanliness: 0, wastewater: 0,
                    })));
                } else {
                    setData([]);
                }
            }
        } catch (err) {
            console.error("GeneralGramSabhaStatusAdmin fetch error:", err);
            setError(err.message || "Failed to load report data");
            if (isDistrictView) {
                setData(HARYANA_DISTRICTS.map((d, i) => ({
                    key: i + 1, sr_no: i + 1,
                    district_name: d.district_name, district_code: d.district_code,
                    total_panchayat: d.total_panchayat,
                    total_gp_conducted_gs: 0, development_work: 0,
                    amrit_sarovar: 0, drug_addiction: 0,
                    drinking_water: 0, cleanliness: 0, wastewater: 0,
                })));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchReport(); }, [dateFilter]);

    /* ================================================================
       OPEN BLOCK MODAL  (Admin / CEO: click Total Panchayat)
    ================================================================ */
    const openBlockModal = async (district) => {
        setBlockPage(1); setBlockPgSize(10);
        setBlockTitle(`Block-wise General Gram Sabha Status : ${district.district_name}`);
        setBlockOpen(true); setBlockData([]); setBlockLoading(true);
        try {
            const res = await getGeneralGramSabhaStatusAdmin(token, buildPayload({
                level: "block",
                district_code: district.district_code,
            }));
            if (res?.success && Array.isArray(res.data) && res.data.length) {
                setBlockData(res.data.map((r, i) => ({
                    ...r, key: r.block_code || `b-${i}`, sr_no: i + 1,
                })));
            } else {
                setBlockData([]);
                message.info("No block-level data available for this district.");
            }
        } catch (err) {
            message.error(err.message || "Failed to fetch block data");
            setBlockData([]);
        } finally { setBlockLoading(false); }
    };

    /* ================================================================
       OPEN GP MODAL  (from district or block)
    ================================================================ */
    const openGPFromDistrict = async (district) => {
        setGpPage(1); setGpPgSize(10);
        setGpTitle(`GP-wise General Gram Sabha Status : ${district.district_name}`);
        setGpOpen(true); setGpData([]); setGpLoading(true);
        try {
            const res = await getGeneralGramSabhaStatusAdmin(token, buildPayload({
                level: "gp",
                district_code: district.district_code,
            }));
            if (res?.success && Array.isArray(res.data) && res.data.length) {
                setGpData(res.data.map((r, i) => ({
                    ...r, key: r.panchayat_code || `gp-${i}`, sr_no: i + 1,
                })));
            } else {
                setGpData([]);
                message.info("No GP-level data available for this district.");
            }
        } catch (err) {
            message.error(err.message || "Failed to fetch GP data");
            setGpData([]);
        } finally { setGpLoading(false); }
    };

    const openGPFromBlock = async (block) => {
        setGpPage(1); setGpPgSize(10);
        setBlockOpen(false);
        setGpTitle(
            `GP-wise General Gram Sabha Status : ${block.block_name || ""} (${block.district_name || ""})`
        );
        setGpOpen(true); setGpData([]); setGpLoading(true);
        try {
            const res = await getGeneralGramSabhaStatusAdmin(token, buildPayload({
                level: "gp",
                district_code: block.district_code,
                block_code: block.block_code,
            }));
            if (res?.success && Array.isArray(res.data) && res.data.length) {
                setGpData(res.data.map((r, i) => ({
                    ...r, key: r.panchayat_code || `gp-${i}`, sr_no: i + 1,
                })));
            } else {
                setGpData([]);
                message.info("No GP-level data available for this block.");
            }
        } catch (err) {
            message.error(err.message || "Failed to fetch GP data");
            setGpData([]);
        } finally { setGpLoading(false); }
    };

    /* ── BDPO: their block's GPs opened directly ── */
    const openGPFromMainBlock = async (block) => {
        setGpPage(1); setGpPgSize(10);
        setGpTitle(
            `GP-wise General Gram Sabha Status : ${block.block_name || ""} (${block.district_name || ""})`
        );
        setGpOpen(true); setGpData([]); setGpLoading(true);
        try {
            const res = await getGeneralGramSabhaStatusAdmin(token, buildPayload({
                level: "gp",
                district_code: block.district_code,
                block_code: block.block_code,
            }));
            if (res?.success && Array.isArray(res.data) && res.data.length) {
                setGpData(res.data.map((r, i) => ({
                    ...r, key: r.panchayat_code || `gp-${i}`, sr_no: i + 1,
                })));
            } else {
                setGpData([]);
                message.info("No GP-level data available for this block.");
            }
        } catch (err) {
            message.error(err.message || "Failed to fetch GP data");
            setGpData([]);
        } finally { setGpLoading(false); }
    };

    /* ================================================================
       COLUMN SETS
    ================================================================ */

    /* ── District columns (Admin, CEO) ── */
    const districtColumns = [
        { title: "Sr. No.", dataIndex: "sr_no", width: 65, fixed: "left", align: "center", sorter: (a, b) => numSort(a, b, "sr_no") },
        { title: "District", dataIndex: "district_name", width: 170, fixed: "left", align: "center", sorter: (a, b) => strSort(a, b, "district_name") },
        {
            title: "Total Panchayat", dataIndex: "total_panchayat", width: 130, align: "center",
            sorter: (a, b) => numSort(a, b, "total_panchayat"),
            render: (val, record) => (
                <span onClick={() => openBlockModal(record)} style={clickStyle} title="Click to view blocks">
                    {val || 0}
                </span>
            ),
        },
        ...AGENDA_COLS,
    ];

    /* ── Block columns (BDPO initial view + Admin/CEO block modal) ── */
    const makeBlockColumns = (onGPClick, showDistrict = true) => [
        { title: "Sr. No.", dataIndex: "sr_no", width: 65, fixed: "left", align: "center", sorter: (a, b) => numSort(a, b, "sr_no") },
        ...(showDistrict ? [{ title: "District Name", dataIndex: "district_name", width: 150, fixed: "left", align: "center", sorter: (a, b) => strSort(a, b, "district_name") }] : []),
        { title: "Block Name", dataIndex: "block_name", width: 180, fixed: "left", align: "center", sorter: (a, b) => strSort(a, b, "block_name") },
        {
            title: "No. of Gram Panchayats", dataIndex: "gp_count", width: 155, align: "center",
            sorter: (a, b) => numSort(a, b, "gp_count"),
            render: (val, record) => (
                <span onClick={() => onGPClick(record)} style={clickStyle} title="Click to view GPs">
                    {val || 0}
                </span>
            ),
        },
        ...AGENDA_COLS,
    ];

    /* ── GP columns (flat, no drill-down) ── */
    const gpColumns = [
        { title: "Sr. No.", dataIndex: "sr_no", width: 65, fixed: "left", align: "center", sorter: (a, b) => numSort(a, b, "sr_no") },
        { title: "District Name", dataIndex: "district_name", width: 140, fixed: "left", align: "center", sorter: (a, b) => strSort(a, b, "district_name") },
        { title: "Block Name", dataIndex: "block_name", width: 160, fixed: "left", align: "center", sorter: (a, b) => strSort(a, b, "block_name") },
        { title: "Gram Panchayat Name", dataIndex: "gp_name", width: 200, fixed: "left", align: "center", sorter: (a, b) => strSort(a, b, "gp_name") },
        ...AGENDA_COLS,
    ];

    /* pick main columns + drill-down behaviour based on role */
    const mainColumns = isGPView
        ? gpColumns
        : isBlockView
            ? makeBlockColumns(openGPFromMainBlock, true)   // BDPO: show district column
            : districtColumns;

    /* ================================================================
       EXCEL EXPORT  (generic)
    ================================================================ */
    const exportExcel = (rows, headerMap, sheetName, fileName) => {
        if (!rows.length) return message.warning("No data to export");
        const excelRows = rows.map((row, i) => {
            const obj = { "Sr. No.": i + 1 };
            headerMap.forEach(({ label, key }) => { obj[label] = row[key] ?? 0; });
            return obj;
        });

        // Grand total row
        const totalRow = { "Sr. No.": "GRAND TOTAL" };
        headerMap.slice(1).forEach(({ label, key }) => {
            totalRow[label] = sumKey(rows, key);
        });
        excelRows.push(totalRow);

        const ws = XLSX.utils.json_to_sheet(excelRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, fileName);
        message.success("Excel exported successfully");
    };

    const DISTRICT_HEADERS = [
        { label: "District", key: "district_name" },
        { label: "Total Panchayat", key: "total_panchayat" },
        { label: "Total GP Conducted GS", key: "total_gp_conducted_gs" },
        { label: "Development Work (All time)", key: "development_work" },
        { label: "Amrit Sarovar (All time)", key: "amrit_sarovar" },
        { label: "Status of Drug Addiction (From 13 Aug)", key: "drug_addiction" },
        { label: "Village Drinking Water Supply Status (From 13 Aug)", key: "drinking_water" },
        { label: "Cleanliness, SWM & Inspection (From 13 Aug)", key: "cleanliness" },
        { label: "Wastewater Management (From 13 Aug)", key: "wastewater" },
    ];

    const BLOCK_HEADERS = [
        { label: "District Name", key: "district_name" },
        { label: "Block Name", key: "block_name" },
        { label: "No. of Gram Panchayats", key: "gp_count" },
        { label: "Total GP Conducted GS", key: "total_gp_conducted_gs" },
        { label: "Development Work (All time)", key: "development_work" },
        { label: "Amrit Sarovar (All time)", key: "amrit_sarovar" },
        { label: "Status of Drug Addiction (From 13 Aug)", key: "drug_addiction" },
        { label: "Village Drinking Water Supply Status (From 13 Aug)", key: "drinking_water" },
        { label: "Cleanliness, SWM & Inspection (From 13 Aug)", key: "cleanliness" },
        { label: "Wastewater Management (From 13 Aug)", key: "wastewater" },
    ];

    const GP_HEADERS = [
        { label: "District Name", key: "district_name" },
        { label: "Block Name", key: "block_name" },
        { label: "Gram Panchayat Name", key: "gp_name" },
        { label: "Total GP Conducted GS", key: "total_gp_conducted_gs" },
        { label: "Development Work (All time)", key: "development_work" },
        { label: "Amrit Sarovar (All time)", key: "amrit_sarovar" },
        { label: "Status of Drug Addiction (From 13 Aug)", key: "drug_addiction" },
        { label: "Village Drinking Water Supply Status (From 13 Aug)", key: "drinking_water" },
        { label: "Cleanliness, SWM & Inspection (From 13 Aug)", key: "cleanliness" },
        { label: "Wastewater Management (From 13 Aug)", key: "wastewater" },
    ];

    const handleExportExcel = () =>
        exportExcel(data,
            isGPView ? GP_HEADERS : isBlockView ? BLOCK_HEADERS : DISTRICT_HEADERS,
            "General GS Status",
            `General_GS_Status_${new Date().toISOString().split("T")[0]}.xlsx`
        );

    const handleExportBlockExcel = () =>
        exportExcel(blockData, BLOCK_HEADERS, "Block GS Status",
            `Block_GS_Status_${new Date().toISOString().split("T")[0]}.xlsx`);

    const handleExportGpExcel = () =>
        exportExcel(gpData, GP_HEADERS, "GP GS Status",
            `GP_GS_Status_${new Date().toISOString().split("T")[0]}.xlsx`);

    /* ── PDF ── */
    const handleExportPDF = () => {
        if (!data.length) return message.warning("No data to export");
        const doc = new jsPDF("landscape");
        doc.setFontSize(13);
        doc.text("General Gram Sabha Status", 14, 14);

        const headers = isGPView
            ? ["Sr.", "District", "Block", "GP Name", "GP Cond. GS", "Dev Work*", "Amrit*", "Drug Addict.", "Drinking Water", "Cleanliness", "Wastewater"]
            : isBlockView
                ? ["Sr.", "District", "Block", "No. GPs", "GP Cond. GS", "Dev Work*", "Amrit*", "Drug Addict.", "Drinking Water", "Cleanliness", "Wastewater"]
                : ["Sr.", "District", "Total\nPanchayat", "GP Cond. GS", "Dev Work*", "Amrit*", "Drug Addict.", "Drinking Water", "Cleanliness", "Wastewater"];

        const bodyRows = data.map((row, i) => {
            const base = isGPView
                ? [i + 1, row.district_name, row.block_name, row.gp_name]
                : isBlockView
                    ? [i + 1, row.district_name, row.block_name, row.gp_count || 0]
                    : [i + 1, row.district_name, row.total_panchayat || 0];
            return [
                ...base,
                row.total_gp_conducted_gs || 0,
                row.development_work || 0,
                row.amrit_sarovar || 0,
                row.drug_addiction || 0,
                row.drinking_water || 0,
                row.cleanliness || 0,
                row.wastewater || 0,
            ];
        });

        autoTable(doc, {
            startY: 22,
            head: [headers],
            body: bodyRows,
            styles: { fontSize: 6.5 },
            headStyles: { fillColor: [22, 119, 255] },
            didDrawPage: (hookData) => {
                doc.setFontSize(7);
                doc.text("* = All-time data (no date filter)", 14, hookData.cursor.y + 5);
            },
        });

        doc.save("General_GS_Status.pdf");
        message.success("PDF exported successfully");
    };

    /* ================================================================
       MAIN TABLE BANNER FIELDS (role-dependent prefix)
    ================================================================ */
    const mainBannerPrefix = isDistrictView
        ? [{ label: "Total Panchayat", key: "total_panchayat" }]
        : isBlockView
            ? [{ label: "Total GPs", key: "gp_count" }]
            : [];

    const reportTitle = isGPView
        ? "General Gram Sabha Status (Gram Panchayat View)"
        : isBlockView
            ? "General Gram Sabha Status (Block-wise)"
            : "General Gram Sabha Status (District-wise)";

    /* ================================================================
       RENDER
    ================================================================ */
    return (
        <div className="report-container">
            <div style={{
                background: "#fff", padding: "24px", borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: "24px",
            }}>
                {/* ── Header ── */}
                <div style={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", marginBottom: "20px",
                }}>
                    <div>
                        <h2 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "4px", color: "#1f1f1f" }}>
                            {reportTitle}
                        </h2>
                        <p style={{ fontSize: "14px", fontWeight: "500", color: "#1677ff", margin: "8px 0 0 0" }}>
                            Total Records: {data.length}
                        </p>
                    </div>

                    <Space>
                        <Button icon={<FilePdfOutlined />} onClick={handleExportPDF}>
                            Export PDF
                        </Button>
                        <Button type="primary" icon={<FileExcelOutlined />} onClick={handleExportExcel}>
                            Export Excel
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={fetchReport}>
                            Refresh
                        </Button>
                    </Space>
                </div>

                {/* ── Error ── */}
                {error && (
                    <Alert message={error} type="error" showIcon style={{ marginBottom: "16px" }} />
                )}

                {/* ── Table ── */}
                {loading ? (
                    <div style={{ textAlign: "center", padding: "50px" }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        columns={mainColumns}
                        dataSource={data}
                        rowKey="key"
                        bordered size="small"
                        scroll={{ x: isGPView ? 2100 : 2000, y: 420 }}
                        pagination={{
                            current: currentPage, pageSize,
                            total: data.length,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50", "100"],
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} records`,
                            onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
                        }}
                    />
                )}

                {/* ── Dynamic Totals Banner (updates with data) ── */}
                {data.length > 0 && (
                    <TotalsBanner
                        data={data}
                        fields={[...mainBannerPrefix, ...AGENDA_BANNER_FIELDS]}
                        recordLabel={isGPView ? "GPs" : isBlockView ? "Blocks" : "Districts"}
                    />
                )}
            </div>


            {/* ================================================================
                BLOCK MODAL  (Admin & CEO only)
            ================================================================ */}
            {isDistrictView && (
                <DrillModal
                    open={blockOpen}
                    onCancel={() => setBlockOpen(false)}
                    title={blockTitle}
                    loading={blockLoading}
                    data={blockData}
                    columns={makeBlockColumns(openGPFromBlock, false)}
                    scroll={{ x: 1950, y: undefined }}
                    recordLabel="Blocks"
                    bannerFields={AGENDA_BANNER_FIELDS}
                    extraBannerFields={[{ label: "Total GPs", key: "gp_count" }]}
                    page={blockPage}
                    pageSize={blockPgSize}
                    onPageChange={(p, s) => { setBlockPage(p); setBlockPgSize(s); }}
                    onExcelExport={handleExportBlockExcel}
                />
            )}


            {/* ================================================================
                GP MODAL  (Admin, CEO & BDPO)
            ================================================================ */}
            {!isGPView && (
                <DrillModal
                    open={gpOpen}
                    onCancel={() => setGpOpen(false)}
                    title={gpTitle}
                    loading={gpLoading}
                    data={gpData}
                    columns={gpColumns}
                    scroll={{ x: 2100, y: undefined }}
                    recordLabel="GPs"
                    bannerFields={AGENDA_BANNER_FIELDS}
                    page={gpPage}
                    pageSize={gpPgSize}
                    onPageChange={(p, s) => { setGpPage(p); setGpPgSize(s); }}
                    onExcelExport={handleExportGpExcel}
                />
            )}
        </div>
    );
};

export default GeneralGramSabhaStatusAdmin;
